import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { ActorRefFrom } from 'xstate';
import { FormLayout, FormRow, FormSection, RichTextArea } from '~/components';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import { JOB_LISTING_STATE } from '~/features/xstateSystem';
import { useCustomForm } from '~/hooks/useCustomForm';
import { InfoMessage } from '../../components/InfoMessage';
import { jobListingMachine } from '../../job-listing/jobListing.machine';
import type { Context as JobListingContext } from '../../job-listing/types';
import { msaasListingsMachine } from '../../msaas-listing/msaasListing.machine';
import type { Context as MsaasListingContext } from '../../msaas-listing/types';
import { resolveRetainedDraft } from '../../step-machine/retainedDraft';
import type { Context as ListingStepContext } from '../../step-machine/types';
import { useFlushStepOnExit } from '../../step-machine/useFlushStepOnExit';
import { emptyAdditionalTermsForm } from './constants';
import { AdditionalTermsFormSchema, AdditionalTermsFormValues } from './types';
import { transformApiToAdditionalTermsForm } from './utils';

const TAGS = CT.employer.listings.additionalTermsForm;

export const AdditionalTermsForm = ({
  footer,
  actor,
}: {
  footer: React.ReactNode;
  actor: ActorRefFrom<typeof msaasListingsMachine> | ActorRefFrom<typeof jobListingMachine>;
}) => {
  const parentContext = useSelector(actor, (s): JobListingContext | MsaasListingContext => {
    if (typeof s === 'object' && s !== null && 'context' in s) {
      return s.context as JobListingContext | MsaasListingContext;
    }
    throw new Error('Invalid state: context not found');
  });
  const { additionalTermsRef, listing } = parentContext;
  const { loading, pendingDraft } = useSelector(additionalTermsRef, (s): ListingStepContext => {
    if (typeof s === 'object' && s !== null && 'context' in s) {
      return s.context as ListingStepContext;
    }
    throw new Error('Invalid state: context not found');
  });

  // [MCV-1543] If the exit flush's write failed, the step machine still holds
  // those values — hydrate from them rather than from a listing.detail the
  // failed write never updated.
  const retainedDraft = resolveRetainedDraft(
    pendingDraft,
    AdditionalTermsFormSchema,
    listing?.detail?.id
  );

  const methods = useCustomForm<AdditionalTermsFormValues>({
    resolver: zodResolver(AdditionalTermsFormSchema),
    defaultValues:
      retainedDraft ??
      (listing?.detail?.id ? transformApiToAdditionalTermsForm(listing) : emptyAdditionalTermsForm),
  });

  const {
    handleSubmit,
    setValue,
    getValues,
    formState: { isDirty },
  } = methods;

  // [MCV-1543] A recovered draft is by definition not persisted, and hydrating
  // from it leaves react-hook-form undirty, so `isDirty` alone would let the
  // step be left again — forwards or backwards — without ever writing it.
  const hasUnsavedWork = isDirty || retainedDraft !== undefined;

  // [MCV-1543] Back-navigation unmounts this form without saving anything.
  useFlushStepOnExit<AdditionalTermsFormValues>({
    save: (values) => additionalTermsRef.send({ type: 'ON_SAVE', payload: { values } }),
    isDirty: hasUnsavedWork,
    getValues,
    schema: AdditionalTermsFormSchema,
    getCurrentStep: () => actor.getSnapshot().context.currentStep,
  });

  const isJobListing = actor.id === JOB_LISTING_STATE; // Set in marketState machine

  useEffect(() => {
    if (listing) {
      if (isJobListing) {
        setValue('listingId', listing.id);
      } else {
        setValue('msaasListingId', listing.id);
      }
      setValue('id', listing.detail?.id);
    }
  }, [listing]);

  const onSubmitSuccess = (values: AdditionalTermsFormValues) => {
    if (hasUnsavedWork) {
      additionalTermsRef.send({ type: 'ON_UPDATE', payload: { values } });
    } else {
      actor.send({ type: 'ON_NEXT' });
    }
  };

  const onSubmitError = (err: unknown) => {
    console.log('Additional Terms Form Submit Error: ', err, methods.getValues());
  };

  if (!listing?.detail?.id || loading) {
    return <LoadingSpinner />;
  }

  return (
    <FormProvider {...methods}>
      {/* Sits beside the <form> rather than inside its FormLayout, but the
        wizard body is itself a `gap-5` column, so the banner's own margins
        would add to that gap exactly the same way. */}
      <InfoMessage
        withOwnMargin={false}
        message={
          'Optional Section: Add any bonus structures, administrative costs, or infrastructure requirements.'
        }
      />
      <form onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}>
        <FormLayout>
          <FormSection componentsPerLine={1}>
            <FormRow>
              <RichTextArea
                name={'bonusesTxt'}
                cyTag={TAGS.bonusesTxt}
                label={'Bonus Structure'}
                description={'Examples: Staffing fill rates, turnaround time, quality metrics'}
              />
            </FormRow>
            <FormRow>
              <RichTextArea
                name={'adminCostsTxt'}
                cyTag={TAGS.adminCostsTxt}
                label={'Administrative Costs'}
                description={'Examples: Billing, malpractice, credentialing support.'}
              />
            </FormRow>
            <FormRow>
              <RichTextArea
                name={'infrastructureTxt'}
                cyTag={TAGS.infrastructureTxt}
                label={'Infrastructure Requirements'}
                description={'Examples: Equipment, IT, compliance tools, etc.'}
              />
            </FormRow>
            <FormRow>
              <RichTextArea
                // [MCV-1543] Registered as `otherTxt`, which is not a key on
                // AdditionalTermsFormSchema — zod stripped it, so Extra Terms was
                // dropped on submit and never re-displayed on edit. The schema,
                // the API transform and the cy tag all say `extraTxt`.
                name={'extraTxt'}
                cyTag={TAGS.extraTxt}
                label={'Extra Terms'}
                description={'Any extra contract information not covered by other contract text.'}
              />
            </FormRow>
          </FormSection>
          {footer}
        </FormLayout>
      </form>
    </FormProvider>
  );
};
